/**
 * containerStyles — how a loot container is drawn in the flat world.
 *
 * The mechanic (walk up, hold to open, loot scatters, respawn later) is the
 * server's; this module only turns a container's state into a picture, the way
 * worldTheme does for walls and trailStyles does for trails. `containerView()`
 * is pure and Node-tested: it reduces a world object + the local interaction
 * + the clock to a small view model; `drawContainer()` paints that model.
 *
 * States → look (style `crate`):
 *   idle        solid box, lighter lid band, accent emblem
 *   interacting idle + an accent ring filling with the local hold progress
 *   opening     the lid slides off over `animationDurationMs`
 *   destroyed   same lid-open as opening (damage-mode kill, from the hit time)
 *   opened      lid off, body dimmed, no emblem (it has been looted)
 *   cooldown    faint dashed outline + a thin ring that fills until respawn
 *
 * Style `case` (config/lootContainers.json `visual.style: 'case'`) is an armored
 * supply case whose tier reads by silhouette: basic/rare are rounded squares,
 * legendary is an octagon with corner studs. See its own header comment below.
 *
 * Adding a look is `registerContainerStyle(id, { draw })` plus `visual.style`
 * in config/lootContainers.json; `visual.accent` colours the lid and rings.
 */
import { Movement } from '../../../shared/Movement.js';
import { toColor } from './trailStyles.js';

const registry = new Map();

export function registerContainerStyle(id, style) {
  if (!id || typeof style?.draw !== 'function') throw new Error(`container style ${id} needs a draw()`);
  registry.set(id, style);
}

export function getContainerStyle(id) {
  return registry.get(id) || registry.get('crate');
}

export const CONTAINER_COLORS = Object.freeze({
  body: 0x4b4f5c,
  bodyEdge: 0x1c1c22,
  lid: 0x6b7080,
  outline: 0x9aa0ad,
  accent: 0xf5c542
});

const clamp01 = (v) => Math.max(0, Math.min(1, v));

/**
 * How big the crate is drawn relative to its interaction box — and, because the
 * collider is built from the same number, how big it is to walk into.
 *
 * This is NOT cosmetic. It used to be applied here only, while shared/Movement.js
 * collided against the unscaled interaction box, which wrapped every container in
 * an invisible skirt (19px a side on a basic chest): you stopped short of the
 * picture and could not fit through gaps that looked open. The size now lives in
 * shared code so the drawn crate and the collider cannot drift apart again.
 * Per-type override: `visual.scale` in config/lootContainers.json.
 * @see Movement.CONTAINER_BODY_SCALE
 */
export const CONTAINER_DRAW_SCALE = Movement.CONTAINER_BODY_SCALE;

/**
 * Reduce a container world object to what the style needs.
 * @param {object} obj - world object (objectType 'lootContainer')
 * @param {object|null} interaction - gameState.lootContainerInteraction for the local player
 *        ({ activeContainerId, progress, isHolding })
 * @param {number} serverNow - current server-clock ms (gameState.serverNow()); the
 *        object's timestamps are server clock too
 */
export function containerView(obj, interaction, serverNow, hitsInfo = null) {
  const state = obj.state || 'idle';
  const t0 = obj.animationStateStartTime ?? obj.stateChangeTime ?? serverNow;
  const since = Math.max(0, serverNow - t0);
  const openMs = obj.animationDurationMs || 500;
  const respawnMs = obj.respawnMs || 0;
  const holding = interaction && interaction.activeContainerId === obj.id && interaction.isHolding ? clamp01(interaction.progress || 0) : 0;
  // The one body box, shared with buildColliders: what is drawn is what you hit.
  // Everything the styles derive (lid band, emblem, ring radius, cooldown dashes)
  // follows from w/h, so this keeps the whole picture in proportion.
  const body = Movement.containerBodySize(obj);

  // Damage-mode hit pips + hit flash. hitsInfo is client-local (from the
  // lootContainerDamaged socket event), keyed by performance.now() like the
  // rest of the client's visual timers — not server-authoritative state.
  const hasHits = hitsInfo && typeof hitsInfo.maxHits === 'number' && hitsInfo.maxHits > 0;
  const maxHits = hasHits ? hitsInfo.maxHits : 0;
  const remaining = hasHits ? Math.max(0, Math.min(maxHits, hitsInfo.remainingHits ?? maxHits)) : 0;
  const msSinceHit = hasHits && hitsInfo.lastHitAt ? (performance.now() - hitsInfo.lastHitAt) : Infinity;
  const flash = msSinceHit < 150 ? 1 - (msSinceHit / 150) : 0;

  return {
    x: obj.x,
    y: obj.y,
    w: body.width,
    h: body.height,
    state,
    style: obj.visual?.style || 'crate',
    accent: toColor(obj.visual?.accent, CONTAINER_COLORS.accent),
    holdProgress: state === 'idle' || state === 'interacting' ? holding : 0,
    openProgress: state === 'opening' || state === 'destroyed'
      ? clamp01(since / openMs)
      : state === 'opened' || state === 'cooldown' ? 1 : 0,
    respawnProgress: state === 'cooldown' && respawnMs > 0 ? clamp01(since / respawnMs) : 0,
    // The server does not send an `isHidden` flag; a container on cooldown reports
    // animationState 'hidden' instead. Accept either so the flag is live in game.
    hidden: !!obj.isHidden || obj.animationState === 'hidden',
    tier: obj.visual?.tier || null,
    maxHits,
    remainingHits: state === 'idle' ? remaining : maxHits,
    flash
  };
}

/** Stable string for dirty-checking: redraw only when this changes. */
export function containerViewKey(v) {
  // Progress values are bucketed so a static container costs nothing per frame.
  return `${v.state}|${v.hidden ? 1 : 0}|${Math.round(v.holdProgress * 60)}|${Math.round(v.openProgress * 30)}|${Math.round(v.respawnProgress * 60)}|${v.remainingHits}/${v.maxHits}|${Math.round(v.flash * 20)}`;
}

/** Draw the view with its style (falls back to `crate`). */
export function drawContainer(g, view) {
  getContainerStyle(view.style).draw(g, view);
}

// ───────────────────────── shared: hit pips ─────────────────────────

const PIP_W = 10;
const PIP_H = 5;
const PIP_GAP = 4;

/**
 * One pip per hit, centred under the body: spent ones dark, the rest in the
 * accent. Drawn only once a hit has landed, so an untouched chest looks clean.
 */
export function drawHitPips(g, v, color) {
  if (!(v.maxHits > 0) || v.remainingHits >= v.maxHits) return;
  const total = v.maxHits * PIP_W + (v.maxHits - 1) * PIP_GAP;
  const x0 = v.x - total / 2;
  const y = v.y + v.h / 2 + 10;
  const spent = v.maxHits - v.remainingHits;
  for (let i = 0; i < v.maxHits; i++) {
    g.rect(x0 + i * (PIP_W + PIP_GAP), y, PIP_W, PIP_H);
    g.fill(i < spent ? { color: 0x000000, alpha: 0.4 } : { color, alpha: 0.95 });
  }
}

// ───────────────────────── built-in: crate ─────────────────────────

function ring(g, x, y, r, progress, color, width, alpha) {
  if (progress <= 0) return;
  const start = -Math.PI / 2;
  g.moveTo(x + Math.cos(start) * r, y + Math.sin(start) * r); // begin a fresh path: no line from the last shape
  g.arc(x, y, r, start, start + progress * Math.PI * 2);
  g.stroke({ width, color, alpha, cap: 'round' });
}

registerContainerStyle('crate', {
  draw(g, v) {
    const { x, y, w, h, accent } = v;
    const half = { w: w / 2, h: h / 2 };
    const C = CONTAINER_COLORS;

    if (v.state === 'cooldown') {
      // Where it will come back: faint dashed outline and a respawn ring.
      const dash = 14; const gap = 10;
      const perim = [[-half.w, -half.h, half.w, -half.h], [half.w, -half.h, half.w, half.h], [half.w, half.h, -half.w, half.h], [-half.w, half.h, -half.w, -half.h]];
      for (const [x0, y0, x1, y1] of perim) {
        const len = Math.hypot(x1 - x0, y1 - y0); const ux = (x1 - x0) / len; const uy = (y1 - y0) / len;
        for (let d = 0; d < len; d += dash + gap) {
          const e = Math.min(len, d + dash);
          g.moveTo(x + x0 + ux * d, y + y0 + uy * d); g.lineTo(x + x0 + ux * e, y + y0 + uy * e);
        }
      }
      g.stroke({ width: 3, color: C.outline, alpha: 0.35 });
      ring(g, x, y, Math.max(half.w, half.h) + 14, v.respawnProgress, accent, 4, 0.5);
      return;
    }

    const opened = v.openProgress >= 1;
    const bodyAlpha = opened ? 0.55 : 1;

    // Shadow + body
    g.rect(x - half.w + 8, y - half.h + 10, w, h);
    g.fill({ color: 0x000000, alpha: 0.25 * bodyAlpha });
    g.rect(x - half.w, y - half.h, w, h);
    g.fill({ color: C.body, alpha: bodyAlpha });
    g.stroke({ width: 4, color: C.bodyEdge, alpha: bodyAlpha });

    // Lid: a band across the top third that slides up and fades as the crate opens.
    const lidH = h * 0.34;
    const slide = v.openProgress * (lidH + 18);
    const lidAlpha = 1 - v.openProgress * 0.7;
    if (lidAlpha > 0.05) {
      g.rect(x - half.w + 4, y - half.h + 4 - slide, w - 8, lidH);
      g.fill({ color: C.lid, alpha: lidAlpha });
      g.rect(x - half.w + 4, y - half.h + 4 - slide + lidH - 8, w - 8, 8);
      g.fill({ color: accent, alpha: lidAlpha });
    }

    // Emblem (diamond) on the body while there is loot inside.
    if (!opened) {
      const r = Math.min(w, h) * 0.14; const cy = y + h * 0.12;
      g.poly([x, cy - r, x + r, cy, x, cy + r, x - r, cy]);
      g.fill({ color: accent, alpha: 0.9 });
    }

    if (!opened) drawHitPips(g, v, accent);

    // Hit flash: a brief white overlay that fades over 150ms.
    if (!opened && v.flash > 0) {
      g.rect(x - half.w, y - half.h, w, h);
      g.fill({ color: 0xffffff, alpha: v.flash * 0.6 });
    }

    // Hold progress ring (local player only).
    ring(g, x, y, Math.max(half.w, half.h) + 14, v.holdProgress, accent, 6, 0.9);
  }
});

// ───────────────────────── built-in: case ─────────────────────────
//
// Armored supply cases, one silhouette per tier so a Legendary reads across the
// map (and to colour-blind players) by shape, not only by its gold accent:
//   basic      rounded square, thin inset outline, small centre dot
//   rare       rounded square, thick inset outline, centre disc, two side vents
//   legendary  octagon, inset octagon, disc with a ring, four corner studs
// config: visual { style: 'case', tier, accent }.

const CASE_COLORS = Object.freeze({ body: 0x2b2f36, edge: 0x1c1c22 });

/** Octagon with corners cut by `c`, centred on (x, y): 8 points, 16 numbers. */
function octagon(x, y, hw, hh, c) {
  return [
    x - hw + c, y - hh, x + hw - c, y - hh, x + hw, y - hh + c, x + hw, y + hh - c,
    x + hw - c, y + hh, x - hw + c, y + hh, x - hw, y + hh - c, x - hw, y - hh + c
  ];
}

registerContainerStyle('case', {
  draw(g, v) {
    // Where it will come back looks the same for every container.
    if (v.state === 'cooldown') { getContainerStyle('crate').draw(g, v); return; }

    const { x, y, w, h, accent } = v;
    const hw = w / 2; const hh = h / 2; const s = Math.min(w, h);
    const tier = v.tier === 'legendary' || v.tier === 'basic' ? v.tier : 'rare';
    const opened = v.openProgress >= 1;
    const bodyAlpha = opened ? 0.55 : 1;

    const shape = (dx, dy, inset) => {
      if (tier === 'legendary') {
        g.poly(octagon(x + dx, y + dy, hw - inset, hh - inset, Math.max(2, s * 0.24 - inset * 0.4)));
      } else {
        const radius = Math.max(2, s * (tier === 'basic' ? 0.22 : 0.2) - inset);
        g.roundRect(x - hw + inset + dx, y - hh + inset + dy, w - inset * 2, h - inset * 2, radius);
      }
    };

    // Shadow + body
    shape(8, 10, 0);
    g.fill({ color: 0x000000, alpha: 0.25 * bodyAlpha });
    shape(0, 0, 0);
    g.fill({ color: CASE_COLORS.body, alpha: bodyAlpha });
    g.stroke({ width: 4, color: CASE_COLORS.edge, alpha: bodyAlpha });

    if (!opened) {
      // The inner panel is the lid: it lifts and fades as the case opens.
      const lift = v.openProgress * 18;
      const panelAlpha = 1 - v.openProgress * 0.7;

      shape(0, -lift, s * (tier === 'basic' ? 0.11 : 0.1));
      g.stroke({ width: tier === 'basic' ? 2 : 3, color: accent, alpha: panelAlpha });

      g.circle(x, y - lift, s * (tier === 'basic' ? 0.11 : tier === 'legendary' ? 0.13 : 0.14));
      g.fill({ color: accent, alpha: 0.95 * panelAlpha });

      if (tier === 'legendary') {
        g.circle(x, y - lift, s * 0.21);
        g.stroke({ width: 2, color: accent, alpha: 0.8 * panelAlpha });
        const k = s * 0.3;
        for (const [sx, sy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
          g.circle(x + sx * k, y - lift + sy * k, s * 0.04);
          g.fill({ color: accent, alpha: panelAlpha });
        }
      } else if (tier === 'rare') {
        for (const sx of [-1, 1]) {
          g.rect(x + sx * s * 0.3 - s * 0.05, y - lift - s * 0.07, s * 0.1, s * 0.14);
          g.fill({ color: accent, alpha: panelAlpha });
        }
      }

      drawHitPips(g, v, accent);

      if (v.flash > 0) {
        shape(0, 0, 0);
        g.fill({ color: 0xffffff, alpha: v.flash * 0.6 });
      }
    }

    ring(g, x, y, Math.max(hw, hh) + 14, v.holdProgress, accent, 6, 0.9);
  }
});
